import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LocationSelector from '../components/common/LocationSelector';
import Button from '../components/common/Button';
import { onboardClinic } from '../services/api';
import { GOOGLE_MAPS_API_KEY } from '../config';

const OnboardNewClinic = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [autocomplete, setAutocomplete] = useState(null);
    const [coordinates, setCoordinates] = useState({ latitude: null, longitude: null });
    const [autoFilledFields, setAutoFilledFields] = useState({ address: false, googleMapUrl: false, pincode: false });
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        cityId: null,
        stateId: null,
        pincode: '',
        phoneNumber: '',
        email: '',
        description: '',
        clinicOpenTime: '',
        clinicCloseTime: '',
        daysOpen: '',
        clinicEstablishedDate: '',
        totalExperience: '',
        googleMapUrl: '',
        areaId: null,
        averageRating: ''
    });

    // Initialize Google Places Autocomplete
    useEffect(() => {
        // Load Google Places API script
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            console.log('Google Places API loaded');
        };
        document.head.appendChild(script);

        return () => {
            // Cleanup
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, []);

    const initializeGooglePlacesAutocomplete = () => {
        if (window.google && window.google.maps && !autocomplete) {
            const input = document.getElementById('clinicSearch');
            if (input) {
                const autocompleteInstance = new window.google.maps.places.Autocomplete(input, {
                    types: ['establishment'],
                    componentRestrictions: { country: 'IN' }, // Restrict to India
                    fields: ['place_id', 'name', 'formatted_address', 'geometry', 'url', 'address_components']
                });

                autocompleteInstance.addListener('place_changed', () => {
                    const place = autocompleteInstance.getPlace();
                    if (place.place_id) {
                        // Auto-fill the Google Maps URL
                        const googleMapsUrl = place.url;
                        
                        // Extract coordinates directly from the place geometry
                        let latitude = null;
                        let longitude = null;
                        
                        if (place.geometry && place.geometry.location) {
                            latitude = place.geometry.location.lat();
                            longitude = place.geometry.location.lng();
                        }
                        
                        // Always auto-fill both address and Google Maps URL
                        setFormData(prev => ({
                            ...prev,
                            address: place.formatted_address || prev.address,
                            googleMapUrl: googleMapsUrl
                        }));

                        // Extract and auto-fill pincode
                        const postalCode = extractPostalCode(place.address_components);
                        if (postalCode) {
                            setFormData(prev => ({
                                ...prev,
                                pincode: postalCode
                            }));
                        }

                        // Store coordinates separately
                        setCoordinates({ latitude, longitude });

                        // Mark fields as auto-filled for visual feedback
                        setAutoFilledFields({
                            address: !!place.formatted_address,
                            googleMapUrl: !!googleMapsUrl,
                            pincode: !!postalCode
                        });

                        // Clear validation errors for auto-filled fields
                        setValidationErrors(prev => ({
                            ...prev,
                            address: '',
                            googleMapUrl: '',
                            pincode: ''
                        }));

                        console.log('Selected place:', place);
                        console.log('Coordinates:', { latitude, longitude });
                        console.log('Auto-filled address:', place.formatted_address);
                        console.log('Auto-filled Google Maps URL:', googleMapsUrl);
                        console.log('Auto-filled pincode:', postalCode);
                    }
                });

                setAutocomplete(autocompleteInstance);
            }
        }
    };

    // Validation functions
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePincode = (pincode) => {
        const pincodeRegex = /^[1-9][0-9]{5}$/;
        return pincodeRegex.test(pincode);
    };

    const validatePhoneNumber = (phone) => {
        const phoneRegex = /^[6-9][0-9]{9}$/;
        return phoneRegex.test(phone);
    };

    const validateGoogleMapsUrl = (url) => {
        if (!url) return true; // Optional field
        // Updated regex to support various Google Maps URL formats:
        // - https://maps.google.com/...
        // - https://www.google.com/maps/...
        // - https://goo.gl/maps/...
        // - https://maps.app.goo.gl/...
        // - https://maps.google.com with query parameters
        const googleMapsRegex = /^https?:\/\/(www\.)?(google\.com\/maps|maps\.google\.com|goo\.gl\/maps|maps\.app\.goo\.gl)\/.*$/;
        return googleMapsRegex.test(url);
    };

    const validateTotalExperience = (experience) => {
        const num = parseInt(experience);
        return !isNaN(num) && num >= 0 && num <= 100;
    };

    const validateAverageRating = (rating) => {
        if (!rating) return true; // Optional field
        const num = parseFloat(rating);
        return !isNaN(num) && num >= 0 && num <= 5;
    };

    // Extract coordinates from Google Maps URL
    const extractCoordinatesFromUrl = (url) => {
        if (!url) return null;

        try {
            // Handle different Google Maps URL formats
            let coordinates = null;

            // Method 1: Extract from @lat,lng format (most common)
            const atPattern = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
            const atMatch = url.match(atPattern);
            if (atMatch) {
                coordinates = {
                    latitude: parseFloat(atMatch[1]),
                    longitude: parseFloat(atMatch[2])
                };
            }

            // Method 2: Extract from query parameters
            if (!coordinates) {
                const urlObj = new URL(url);
                const q = urlObj.searchParams.get('q');
                if (q) {
                    const coordPattern = /(-?\d+\.\d+),(-?\d+\.\d+)/;
                    const coordMatch = q.match(coordPattern);
                    if (coordMatch) {
                        coordinates = {
                            latitude: parseFloat(coordMatch[1]),
                            longitude: parseFloat(coordMatch[2])
                        };
                    }
                }
            }

            // Method 3: Extract from path parameters
            if (!coordinates) {
                const pathPattern = /@(-?\d+\.\d+),(-?\d+\.\d+),/;
                const pathMatch = url.match(pathPattern);
                if (pathMatch) {
                    coordinates = {
                        latitude: parseFloat(pathMatch[1]),
                        longitude: parseFloat(pathMatch[2])
                    };
                }
            }

            // Method 4: Extract from data parameter
            if (!coordinates) {
                const dataPattern = /data=([^&]+)/;
                const dataMatch = url.match(dataPattern);
                if (dataMatch) {
                    try {
                        const decodedData = decodeURIComponent(dataMatch[1]);
                        const coordPattern = /(-?\d+\.\d+),(-?\d+\.\d+)/;
                        const coordMatch = decodedData.match(coordPattern);
                        if (coordMatch) {
                            coordinates = {
                                latitude: parseFloat(coordMatch[1]),
                                longitude: parseFloat(coordMatch[2])
                            };
                        }
                    } catch (e) {
                        // Ignore decoding errors
                    }
                }
            }

            return coordinates;
        } catch (error) {
            console.error('Error extracting coordinates from URL:', error);
            return null;
        }
    };

    // Extract postal code from Google Places address components
    const extractPostalCode = (addressComponents) => {
        if (!addressComponents || !Array.isArray(addressComponents)) {
            return null;
        }

        const postalCodeComponent = addressComponents.find(component => 
            component.types && component.types.includes('postal_code')
        );

        return postalCodeComponent ? postalCodeComponent.long_name : null;
    };

    // Validate specific field
    const validateField = (name, value) => {
        switch (name) {
            case 'email':
                return validateEmail(value) ? '' : 'Please enter a valid email address';
            case 'pincode':
                return validatePincode(value) ? '' : 'Please enter a valid 6-digit pincode';
            case 'phoneNumber':
                return validatePhoneNumber(value) ? '' : 'Please enter a valid 10-digit phone number starting with 6-9';
            case 'googleMapUrl':
                return validateGoogleMapsUrl(value) ? '' : 'Please enter a valid Google Maps URL';
            case 'totalExperience':
                return validateTotalExperience(value) ? '' : 'Please enter a valid number between 0 and 100';
            case 'averageRating':
                return validateAverageRating(value) ? '' : 'Please enter a valid number between 0 and 5';
            default:
                return '';
        }
    };

    // Validate entire form
    const validateForm = () => {
        const errors = {};
        
        // Required field validations
        if (!formData.name.trim()) errors.name = 'Clinic name is required';
        if (!formData.address.trim()) errors.address = 'Address is required';
        if (!formData.cityId) errors.cityId = 'Please select a city';
        if (!formData.stateId) errors.stateId = 'Please select a state';
        if (!formData.pincode) errors.pincode = 'Pincode is required';
        if (!formData.phoneNumber) errors.phoneNumber = 'Phone number is required';
        if (!formData.email) errors.email = 'Email is required';
        if (!formData.clinicOpenTime) errors.clinicOpenTime = 'Opening time is required';
        if (!formData.clinicCloseTime) errors.clinicCloseTime = 'Closing time is required';
        if (!formData.daysOpen) errors.daysOpen = 'Please select at least one day';
        if (!formData.clinicEstablishedDate) errors.clinicEstablishedDate = 'Establishment date is required';
        if (!formData.totalExperience) errors.totalExperience = 'Total experience is required';
        if (!formData.googleMapUrl) errors.googleMapUrl = 'Google Maps URL is required';
        if (!formData.averageRating) errors.averageRating = 'Average rating is required';

        // Format validations
        if (formData.email && !validateEmail(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }
        if (formData.pincode && !validatePincode(formData.pincode)) {
            errors.pincode = 'Please enter a valid 6-digit pincode';
        }
        if (formData.phoneNumber && !validatePhoneNumber(formData.phoneNumber)) {
            errors.phoneNumber = 'Please enter a valid 10-digit phone number starting with 6-9';
        }
        if (formData.googleMapUrl && !validateGoogleMapsUrl(formData.googleMapUrl)) {
            errors.googleMapUrl = 'Please enter a valid Google Maps URL';
        }
        if (formData.totalExperience && !validateTotalExperience(formData.totalExperience)) {
            errors.totalExperience = 'Please enter a valid number between 0 and 100';
        }
        if (formData.averageRating && !validateAverageRating(formData.averageRating)) {
            errors.averageRating = 'Please enter a valid number between 0 and 5';
        }

        // Business logic validations
        if (formData.clinicOpenTime && formData.clinicCloseTime) {
            if (formData.clinicOpenTime >= formData.clinicCloseTime) {
                errors.clinicCloseTime = 'Closing time must be after opening time';
            }
        }

        if (formData.clinicEstablishedDate) {
            const establishedDate = new Date(formData.clinicEstablishedDate);
            const today = new Date();
            if (establishedDate > today) {
                errors.clinicEstablishedDate = 'Establishment date cannot be in the future';
            }
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleLocationSelect = ({ stateId, cityId }) => {
        setFormData(prev => ({
            ...prev,
            stateId,
            cityId
        }));
        // Clear validation errors for location fields
        setValidationErrors(prev => ({
            ...prev,
            stateId: '',
            cityId: ''
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Reset auto-filled flag if user manually edits the field
        if (name === 'address' || name === 'googleMapUrl' || name === 'pincode') {
            setAutoFilledFields(prev => ({
                ...prev,
                [name]: false
            }));
        }

        // Real-time validation
        const fieldError = validateField(name, value);
        setValidationErrors(prev => ({
            ...prev,
            [name]: fieldError
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            setError('Please fix the validation errors before submitting');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Send Google Maps URL to backend for coordinate extraction
            const clinicData = {
                ...formData,
                cityId: parseFloat(formData.cityId),
                stateId: parseFloat(formData.stateId),
                totalExperience: parseInt(formData.totalExperience),
                averageRating: parseFloat(formData.averageRating) || 0,
                clinicOpenTime: formData.clinicOpenTime + ':00', // Add seconds for LocalTime
                clinicCloseTime: formData.clinicCloseTime + ':00', // Add seconds for LocalTime
                latitude: coordinates.latitude,
                longitude: coordinates.longitude
            };

            const response = await onboardClinic(clinicData);
            if (response.success) {
                navigate('/clinics/onboard');
            } else {
                setError(response.message || 'Failed to onboard clinic');
            }
        } catch (err) {
            setError('Failed to onboard clinic. Please try again.');
            console.error('Error onboarding clinic:', err);
        } finally {
            setLoading(false);
        }
    };

    const getInputClassName = (fieldName) => {
        const baseClass = "w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500";
        const errorClass = "border-red-300 focus:border-red-500 focus:ring-red-500";
        const normalClass = "border-gray-300";
        
        return `${baseClass} ${validationErrors[fieldName] ? errorClass : normalClass}`;
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-2xl mx-auto">
                <h1 className="text-2xl font-bold text-gray-900 mb-8">Onboard New Clinic</h1>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Information */}
                    <div className="bg-white rounded-lg shadow p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
                        
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                Clinic Name*
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                className={getInputClassName('name')}
                            />
                            {validationErrors.name && (
                                <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                                Description
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={3}
                                className={getInputClassName('description')}
                            />
                        </div>

                        <div>
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                                Address*
                            </label>
                            <textarea
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                required
                                rows={2}
                                className={getInputClassName('address')}
                            />
                            {validationErrors.address && (
                                <p className="mt-1 text-sm text-red-600">{validationErrors.address}</p>
                            )}
                            {autoFilledFields.address && (
                                <p className="mt-1 text-sm text-green-600 flex items-center">
                                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    Address auto-filled from Google Places
                                </p>
                            )}
                        </div>

                        <LocationSelector onLocationSelect={handleLocationSelect} showFetchButton={false} />
                        {(validationErrors.stateId || validationErrors.cityId) && (
                            <p className="mt-1 text-sm text-red-600">Please select both state and city</p>
                        )}

                        <div>
                            <label htmlFor="pincode" className="block text-sm font-medium text-gray-700 mb-1">
                                Pincode*
                            </label>
                            <input
                                type="text"
                                id="pincode"
                                name="pincode"
                                value={formData.pincode}
                                onChange={handleInputChange}
                                required
                                maxLength="6"
                                placeholder="Enter 6-digit pincode"
                                className={getInputClassName('pincode')}
                            />
                            {validationErrors.pincode && (
                                <p className="mt-1 text-sm text-red-600">{validationErrors.pincode}</p>
                            )}
                            {autoFilledFields.pincode && (
                                <p className="mt-1 text-sm text-green-600 flex items-center">
                                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    Pincode auto-filled from Google Places
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="bg-white rounded-lg shadow p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
                        
                        <div>
                            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                Phone Number*
                            </label>
                            <input
                                type="tel"
                                id="phoneNumber"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleInputChange}
                                required
                                maxLength="10"
                                placeholder="Enter 10-digit phone number"
                                className={getInputClassName('phoneNumber')}
                            />
                            {validationErrors.phoneNumber && (
                                <p className="mt-1 text-sm text-red-600">{validationErrors.phoneNumber}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email*
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                placeholder="Enter valid email address"
                                className={getInputClassName('email')}
                            />
                            {validationErrors.email && (
                                <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                            )}
                        </div>
                    </div>

                    {/* Location Information */}
                    <div className="bg-white rounded-lg shadow p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Location Information</h2>
                        
                        <div>
                            <label htmlFor="clinicSearch" className="block text-sm font-medium text-gray-700 mb-1">
                                Search for your clinic location*
                            </label>
                            <input
                                type="text"
                                id="clinicSearch"
                                placeholder="Search for your clinic on Google Maps..."
                                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                onFocus={() => initializeGooglePlacesAutocomplete()}
                            />
                            <p className="mt-1 text-sm text-gray-500">
                                Start typing to search for your clinic location. Select from the suggestions to auto-fill address, Google Maps URL, and pincode.
                            </p>
                        </div>

                        <div>
                            <label htmlFor="googleMapUrl" className="block text-sm font-medium text-gray-700 mb-1">
                                Google Maps URL*
                            </label>
                            <input
                                type="url"
                                id="googleMapUrl"
                                name="googleMapUrl"
                                value={formData.googleMapUrl}
                                onChange={handleInputChange}
                                required
                                placeholder="https://maps.google.com/..."
                                className={getInputClassName('googleMapUrl')}
                            />
                            {validationErrors.googleMapUrl && (
                                <p className="mt-1 text-sm text-red-600">{validationErrors.googleMapUrl}</p>
                            )}
                            {autoFilledFields.googleMapUrl && (
                                <p className="mt-1 text-sm text-green-600 flex items-center">
                                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    Google Maps URL auto-filled from Google Places
                                </p>
                            )}
                            <p className="mt-1 text-sm text-gray-500">
                                Enter the Google Maps URL for your clinic location. 
                                <span className="text-blue-600 font-medium"> Coordinates will be automatically extracted on the server!</span>
                            </p>
                        </div>
                    </div>

                    {/* Business Information */}
                    <div className="bg-white rounded-lg shadow p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h2>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="clinicOpenTime" className="block text-sm font-medium text-gray-700 mb-1">
                                    Opening Time*
                                </label>
                                <input
                                    type="time"
                                    id="clinicOpenTime"
                                    name="clinicOpenTime"
                                    value={formData.clinicOpenTime}
                                    onChange={handleInputChange}
                                    required
                                    className={getInputClassName('clinicOpenTime')}
                                />
                                {validationErrors.clinicOpenTime && (
                                    <p className="mt-1 text-sm text-red-600">{validationErrors.clinicOpenTime}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="clinicCloseTime" className="block text-sm font-medium text-gray-700 mb-1">
                                    Closing Time*
                                </label>
                                <input
                                    type="time"
                                    id="clinicCloseTime"
                                    name="clinicCloseTime"
                                    value={formData.clinicCloseTime}
                                    onChange={handleInputChange}
                                    required
                                    className={getInputClassName('clinicCloseTime')}
                                />
                                {validationErrors.clinicCloseTime && (
                                    <p className="mt-1 text-sm text-red-600">{validationErrors.clinicCloseTime}</p>
                                )}
                            </div>
                        </div>

                        <div>
                            <label htmlFor="daysOpen" className="block text-sm font-medium text-gray-700 mb-1">
                                Days Open*
                            </label>
                            <select
                                multiple
                                id="daysOpen"
                                name="daysOpen"
                                value={formData.daysOpen.split(',')}
                                onChange={(e) => {
                                    const selectedDays = Array.from(e.target.selectedOptions, option => option.value);
                                    setFormData(prev => ({
                                        ...prev,
                                        daysOpen: selectedDays.join(',')
                                    }));
                                    // Clear validation error
                                    setValidationErrors(prev => ({
                                        ...prev,
                                        daysOpen: ''
                                    }));
                                }}
                                required
                                className={getInputClassName('daysOpen')}
                            >
                                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                                    <option key={day} value={day}>{day}</option>
                                ))}
                            </select>
                            {validationErrors.daysOpen && (
                                <p className="mt-1 text-sm text-red-600">{validationErrors.daysOpen}</p>
                            )}
                            <p className="mt-1 text-sm text-gray-500">Hold Ctrl/Cmd to select multiple days</p>
                        </div>

                        <div>
                            <label htmlFor="clinicEstablishedDate" className="block text-sm font-medium text-gray-700 mb-1">
                                Establishment Date*
                            </label>
                            <input
                                type="date"
                                id="clinicEstablishedDate"
                                name="clinicEstablishedDate"
                                value={formData.clinicEstablishedDate}
                                onChange={handleInputChange}
                                required
                                max={new Date().toISOString().split('T')[0]}
                                className={getInputClassName('clinicEstablishedDate')}
                            />
                            {validationErrors.clinicEstablishedDate && (
                                <p className="mt-1 text-sm text-red-600">{validationErrors.clinicEstablishedDate}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="totalExperience" className="block text-sm font-medium text-gray-700 mb-1">
                                Total Experience (years)*
                            </label>
                            <input
                                type="number"
                                id="totalExperience"
                                name="totalExperience"
                                value={formData.totalExperience}
                                onChange={handleInputChange}
                                required
                                min="0"
                                max="100"
                                placeholder="Enter years of experience"
                                className={getInputClassName('totalExperience')}
                            />
                            {validationErrors.totalExperience && (
                                <p className="mt-1 text-sm text-red-600">{validationErrors.totalExperience}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="averageRating" className="block text-sm font-medium text-gray-700 mb-1">
                                Average Rating (out of 5)*
                            </label>
                            <input
                                type="number"
                                id="averageRating"
                                name="averageRating"
                                value={formData.averageRating}
                                onChange={handleInputChange}
                                required
                                min="0"
                                max="5"
                                placeholder="Enter average rating"
                                className={getInputClassName('averageRating')}
                            />
                            {validationErrors.averageRating && (
                                <p className="mt-1 text-sm text-red-600">{validationErrors.averageRating}</p>
                            )}
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                            {error}
                        </div>
                    )}

                    {/* Submit Button */}
                    <div className="flex justify-end space-x-3">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => navigate('/clinics/onboard')}
                            loading={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            loading={loading}
                        >
                            Onboard Clinic
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OnboardNewClinic; 